'use client';

import { useState } from 'react';
import { useWorkspace } from '@/lib/workspace';
import { useUser } from '@/hooks/use-user';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Check, ChevronsUpDown, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WorkspaceSwitcherProps {
  className?: string;
  showLabel?: boolean;
}

export function WorkspaceSwitcher({ className, showLabel = false }: WorkspaceSwitcherProps) {
  const [open, setOpen] = useState(false);
  const { user } = useUser();
  const { activeWorkspace, workspaces, isLoading, switchWorkspace } = useWorkspace();

  const handleSelect = async (workspaceId: string) => {
    if (workspaceId === activeWorkspace?.id) {
      setOpen(false);
      return;
    }

    try {
      await switchWorkspace(workspaceId);
      setOpen(false);
    } catch (error) {
      console.error('Failed to switch workspace:', error);
      // Could show a toast error here
    }
  };

  const getWorkspaceInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 1);
  };

  // Loading state - maintain consistent height
  if (isLoading) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <div className="w-8 h-8 rounded-lg bg-muted animate-pulse" />
        {showLabel && (
          <div className="h-4 w-20 bg-muted animate-pulse rounded" />
        )}
      </div>
    );
  }

  // Not logged in state - show placeholder with same dimensions
  if (!user) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <div className="w-8 h-8 rounded-lg bg-muted/30 border border-dashed border-muted-foreground/20 flex items-center justify-center">
          <Building2 className="h-4 w-4 text-muted-foreground/40" />
        </div>
        {showLabel && (
          <div className="flex-1 text-left">
            <span className="text-sm text-muted-foreground/60">Sign in</span>
          </div>
        )}
      </div>
    );
  }

  // No workspace available - show placeholder for logged-in user
  if (!activeWorkspace || !workspaces) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
          <Building2 className="h-4 w-4 text-muted-foreground" />
        </div>
        {showLabel && (
          <div className="flex-1 text-left">
            <span className="text-sm text-muted-foreground">No workspace</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'justify-start gap-2 font-normal',
            showLabel ? 'w-full' : 'w-auto h-auto p-2',
            className
          )}
        >
          <Avatar className="h-6 w-6">
            <AvatarFallback className="bg-primary text-primary-foreground text-xs">
              {getWorkspaceInitials(activeWorkspace.name)}
            </AvatarFallback>
          </Avatar>
          
          {showLabel && (
            <>
              <span className="truncate flex-1 text-left">
                {activeWorkspace.name}
              </span>
              <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
            </>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className="w-64 p-1" align="start">
        <div className="flex flex-col">
          <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
            Workspaces
          </div>
          {workspaces.map((workspace) => {
            const isActive = workspace.id === activeWorkspace?.id;
            return (
              <Button
                key={workspace.id}
                variant="ghost"
                onClick={() => handleSelect(workspace.id)}
                className={cn(
                  "flex items-center gap-2 h-auto p-2 justify-start font-normal",
                  isActive && "bg-muted text-foreground"
                )}
              >
                <Avatar className="h-6 w-6">
                  <AvatarFallback className={cn(
                    "text-xs",
                    isActive 
                      ? "bg-muted text-muted-foreground" 
                      : "bg-muted text-muted-foreground"
                  )}>
                    {getWorkspaceInitials(workspace.name)}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1 min-w-0 text-left">
                  <div className="font-medium truncate">
                    {workspace.name}
                  </div>
                  {workspace.description && (
                    <div className="text-xs text-muted-foreground truncate">
                      {workspace.description}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  {workspace.user_role && (
                    <span className="text-xs text-muted-foreground capitalize">
                      {workspace.user_role}
                    </span>
                  )}
                  
                  {isActive && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </div>
              </Button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Compact workspace switcher for nav rails - shows only avatar
 */
export function WorkspaceSwitcherCompact({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const { user } = useUser();
  const { activeWorkspace, workspaces, isLoading, switchWorkspace } = useWorkspace();

  const handleSelect = async (workspaceId: string) => {
    if (workspaceId === activeWorkspace?.id) {
      setOpen(false);
      return;
    }

    try {
      await switchWorkspace(workspaceId);
      setOpen(false);
    } catch (error) {
      console.error('Failed to switch workspace:', error);
    }
  };

  const getWorkspaceInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 1);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={cn('flex items-center justify-center', className)}>
        <div className="w-8 h-8 rounded-full bg-muted animate-pulse" />
      </div>
    );
  }

  // Not logged in state
  if (!user) {
    return (
      <div className={cn('flex items-center justify-center', className)}>
        <div className="w-8 h-8 rounded-full bg-muted/30 border border-dashed border-muted-foreground/20 flex items-center justify-center">
          <Building2 className="h-4 w-4 text-muted-foreground/40" />
        </div>
      </div>
    );
  }

  // No workspace available
  if (!activeWorkspace || !workspaces) {
    return (
      <div className={cn('flex items-center justify-center', className)}>
        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
          <Building2 className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex items-center justify-center', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Avatar className="h-8 w-8 cursor-pointer hover:ring-2 hover:ring-ring transition-all">
            <AvatarFallback className="bg-primary text-primary-foreground text-sm">
              {getWorkspaceInitials(activeWorkspace.name)}
            </AvatarFallback>
          </Avatar>
        </PopoverTrigger>
        
        <PopoverContent className="w-64 p-1" align="start">
          <div className="flex flex-col">
            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
              Workspaces
            </div>
            {workspaces.map((workspace) => {
              const isActive = workspace.id === activeWorkspace?.id;
              return (
                <Button
                  key={workspace.id}
                  variant="ghost"
                  onClick={() => handleSelect(workspace.id)}
                  className={cn(
                    "flex items-center gap-2 h-auto p-2 justify-start font-normal",
                    isActive && "bg-muted text-foreground"
                  )}
                >
                  <Avatar className="h-6 w-6">
                    <AvatarFallback className={cn(
                      "text-xs",
                      isActive 
                        ? "bg-muted text-muted-foreground" 
                        : "bg-muted text-muted-foreground"
                    )}>
                      {getWorkspaceInitials(workspace.name)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0 text-left">
                    <div className="font-medium truncate">
                      {workspace.name}
                    </div>
                    {workspace.description && (
                      <div className="text-xs text-muted-foreground truncate">
                        {workspace.description}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    {workspace.user_role && (
                      <span className="text-xs text-muted-foreground capitalize">
                        {workspace.user_role}
                      </span>
                    )}
                    
                    {isActive && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </div>
                </Button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * Full workspace switcher with label for sidebars
 */
export function WorkspaceSwitcherFull({ className }: { className?: string }) {
  return <WorkspaceSwitcher className={className} showLabel={true} />;
} 